import React, { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
interface Props {
  children: ReactNode;
  /**
   * Optional label shown in the fallback UI to help identify which area crashed.
   */
  label?: string;
  /**
   * Optional reset handler invoked when the user clicks "Intentar de nuevo".
   * If not provided, the boundary just clears its internal error state which
   * re-renders the children — useful when the crash is transient (stale data).
   */
  onReset?: () => void;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}
/**
 * Generic error boundary used to prevent a render crash from blanking an
 * entire admin page. Shows a friendly recovery UI with the error message
 * and a reload / retry button. Never throws further up the tree.
 *
 * Production-safe: also logs the error to the console with a clear prefix
 * so we can surface the real cause in the user's browser devtools.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };
  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const label = this.props.label || 'page';
    console.error(`[NOIZA][ErrorBoundary][${label}] render crash:`, error);
    console.error(`[NOIZA][ErrorBoundary][${label}] component stack:`, errorInfo.componentStack);
    this.setState({
      errorInfo
    });
  }
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    this.props.onReset?.();
  };
  handleReload = () => {
    window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'Ocurrió un error inesperado al mostrar esta pantalla.';
      return <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center p-6" data-id="element-2913">
          <div className="max-w-lg w-full bg-white rounded-3xl border-2 border-[#E5DCC9] shadow-lg p-6 sm:p-8" data-id="element-2914">
            <div className="flex items-center gap-3 mb-4" data-id="element-2915">
              <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center shrink-0" data-id="element-2916">
                <AlertTriangle className="w-6 h-6 text-red-600" data-id="element-2917" />
              </div>
              <div data-id="element-2918">
                <h2 className="text-xl font-black text-[#0B0807] leading-tight" data-id="element-2919">
                  Algo salió mal
                </h2>
                <p className="text-sm text-[#6B5E4F] mt-0.5" data-id="element-2920">
                  Esta pantalla tuvo un error y se detuvo para no dañar nada.
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-sm text-red-800 break-words" data-id="element-2921">
              <strong className="block text-xs font-black uppercase tracking-wider mb-1 text-red-700" data-id="element-2922">
                Detalle técnico
              </strong>
              {message}
            </div>

            <div className="flex flex-col sm:flex-row gap-2" data-id="element-2923">
              <button onClick={this.handleReset} className="flex-1 h-12 rounded-xl font-bold text-sm bg-[#0B0807] hover:bg-black text-white flex items-center justify-center gap-2 transition-colors" data-id="element-2924">
                <RefreshCw className="w-4 h-4" data-id="element-2925" />
                Intentar de nuevo
              </button>
              <button onClick={this.handleReload} className="flex-1 h-12 rounded-xl font-bold text-sm bg-white border-2 border-[#E5DCC9] hover:border-[#C6A969] text-[#0B0807] flex items-center justify-center gap-2 transition-colors" data-id="element-2926">
                Recargar la página
              </button>
            </div>

            <p className="text-xs text-[#6B5E4F] mt-4 text-center" data-id="element-2927">
              Si el error continúa, toma una captura de pantalla con la consola
              abierta (F12) y compártela con soporte.
            </p>
          </div>
        </div>;
    }
    return this.props.children;
  }
}